/**
 * Tracer: DVĚ oddělené mapy — telemetrie (rychlý ~1 Hz kanál) a statistiky (pomalý `tracer:state`,
 * na klientu `device.runtime?.statistics`). Pořadí klíčů = pořadí původních publish volání (tracer/index.ts).
 */

import type { MetricValue, TopicMap, TracerStatisticsShape, TracerTelemetryShape } from "@globals/telemetry/types";

const fixed = (digits: number) => (v: MetricValue) => (v as number).toFixed(digits);
const hex4 = (n: number) => n.toString(16).padStart(4, "0");

export const TRACER_TOPIC_MAP: TopicMap<TracerTelemetryShape> = {
  "pv-v": { get: (t) => t.pvVoltageV, format: fixed(2) },
  "pv-a": { get: (t) => t.pvCurrentA, format: fixed(2) },
  "pv-w": { get: (t) => t.pvPowerW, format: fixed(1) },
  "battery-v": { get: (t) => t.batteryVoltageV, format: fixed(2) },
  "battery-charge-a": { get: (t) => t.batteryChargeCurrentA, format: fixed(2) },
  "battery-charge-w": { get: (t) => t.batteryChargePowerW, format: fixed(1) },
  "battery-net-a": { get: (t) => t.batteryNetCurrentA, cond: (t) => t.batteryNetCurrentA !== null, format: fixed(2) },
  "battery-soc": { get: (t) => t.batterySocPercent },
  "battery-temp": { get: (t) => t.batteryTempC, format: fixed(1) },
  "internal-temp": { get: (t) => t.internalTempC, format: fixed(1) },
  "heatsink-temp": { get: (t) => t.heatsinkTempC, format: fixed(1) },
  "ambient-temp": { get: (t) => t.ambientTempC, cond: (t) => t.ambientTempC !== null, format: fixed(1) },
  "load-v": { get: (t) => t.loadVoltageV, format: fixed(2) },
  "load-a": { get: (t) => t.loadCurrentA, format: fixed(2) },
  "load-w": { get: (t) => t.loadPowerW, format: fixed(1) },
  "load-on": { get: (t) => t.loadOn },
  stage: { get: (t) => t.chargingStage },
  running: { get: (t) => t.running },
  fault: { get: (t) => t.fault },
  "alarms-active-count": { get: (t) => t.activeWarnings.length },
  "alarms-bitmap": { get: (t) => `0x${hex4(t.raw.batteryStatus)}${hex4(t.raw.chargingStatus)}` },
};

export const TRACER_STATS_MAP: TopicMap<TracerStatisticsShape> = {
  "energy-gen-today-kwh": { get: (s) => s.energyGeneratedTodayKwh, format: fixed(2) },
  "energy-gen-month-kwh": { get: (s) => s.energyGeneratedMonthKwh, format: fixed(2) },
  "energy-gen-year-kwh": { get: (s) => s.energyGeneratedYearKwh, format: fixed(2) },
  "energy-gen-total-kwh": { get: (s) => s.energyGeneratedTotalKwh, format: fixed(2) },
  "energy-cons-today-kwh": { get: (s) => s.energyConsumedTodayKwh, format: fixed(2) },
  "energy-cons-month-kwh": { get: (s) => s.energyConsumedMonthKwh, format: fixed(2) },
  "energy-cons-year-kwh": { get: (s) => s.energyConsumedYearKwh, format: fixed(2) },
  "energy-cons-total-kwh": { get: (s) => s.energyConsumedTotalKwh, format: fixed(2) },
  "pv-v-max-today": { get: (s) => s.pvVoltageMaxTodayV, format: fixed(2) },
  "pv-v-min-today": { get: (s) => s.pvVoltageMinTodayV, format: fixed(2) },
  "battery-v-max-today": { get: (s) => s.batteryVoltageMaxTodayV, format: fixed(2) },
  "battery-v-min-today": { get: (s) => s.batteryVoltageMinTodayV, format: fixed(2) },
  "co2-reduction": { get: (s) => s.co2ReductionTonnes, format: fixed(3) },
};

/** Suffixy, které se čtou ze statistik (NE z telemetrie) — pro routing klientského live hooku. */
export const TRACER_STATS_SUFFIXES: ReadonlySet<string> = new Set(Object.keys(TRACER_STATS_MAP));
